import { get, uniqBy } from 'lodash';
import queryString from 'query-string';
import * as mime from 'mime-types';
import * as moment from 'moment-timezone';
import { createFetchAction } from '../helpers/fetch';
import { simpleSignError } from '../helpers/simpleSignError';
import { getToken, logout, buildApiUrl, buildTenantApiUri } from '../../helpers';

// Actions
export const GET_ESIGN_DOCS = 'GET_ESIGN_DOCS';
export const GET_TASKLIST_DOCS = 'GET_TASKLIST_DOCS';
export const SAVE_TEMPLATE_METADATA = 'SAVE_TEMPLATE_METADATA';
export const GET_EMPLOYEE_DOCUMENTS = 'GET_EMPLOYEE_DOCUMENTS';
export const GET_DOCUMENT_PREVIEW_URL = 'GET_DOCUMENT_PREVIEW_URL';
export const GET_DOCUMENT_DOWNLOAD_URL = 'GET_DOCUMENT_DOWNLOAD_URL';
export const UPLOAD_EMPLOYEE_DOCUMENT = 'UPLOAD_EMPLOYEE_DOCUMENT';
export const UPLOAD_SIMPLE_SIGNATURE_REQUEST = 'UPLOAD_SIMPLE_SIGNATURE_REQUEST';
export const GET_COMPANY_DOCUMENT_CATEGORIES = 'GET_COMPANY_DOCUMENT_CATEGORIES';
export const GET_EMPLOYEE_DOCUMENT_CATEGORIES = 'GET_EMPLOYEE_DOCUMENT_CATEGORIES';
export const PUSH_RECENT_DOC_CATEGORY = 'PUSH_RECENT_DOC_CATEGORY';
export const UPLOAD_COMPANY_DOCUMENT = 'UPLOAD_COMPANY_DOCUMENT';
export const GET_ONBOARDING_DOC_LIST = 'GET_ONBOARDING_DOC_LIST';
export const SET_SELECTED_DOCUMENT = 'SET_SELECTED_DOCUMENT';
export const UPDATE_COMPANY_DOCUMENT = 'UPDATE_COMPANY_DOCUMENT';
export const UPDATE_EMPLOYEE_DOCUMENT = 'UPDATE_EMPLOYEE_DOCUMENT';
export const DELETE_COMPANY_DOCUMENT = 'DELETE_COMPANY_DOCUMENT';
export const DELETE_EMPLOYEE_DOCUMENT = 'DELETE_EMPLOYEE_DOCUMENT';
export const REFRESH_EMPLOYEE_DOCUMENTS = 'REFRESH_EMPLOYEE_DOCUMENTS';
export const CLEAR_CATEGORIES = 'CLEAR_CATEGORIES';
export const UPDATE_SIGNATURE_REQUEST_STATUS = 'UPDATE_SIGNATURE_REQUEST_STATUS';
export const CREATE_SIMPLE_SIGN_DOCUMENT = 'CREATE_SIMPLE_SIGN_DOCUMENT';
export const SEND_REMINDER_EMAIL = 'SEND_REMINDER_EMAIL';

export const GET_UPLOAD_URL = 'GET_UPLOAD_URL';

// Action Creators
export function getEsignDocs(next, search = '', requestId = '') {
  const token = getToken();
  const companyId = get(window, 'asure.appSettings.selectedCompany.companyId');
  const searchUrlParam = search ? `&search=${encodeURIComponent(search)}` : '';
  const url = next || `${buildApiUrl(token, companyId)}/templates?consolidated=true${searchUrlParam}`;
  return createFetchAction(GET_ESIGN_DOCS, {
    url,
    method: 'GET',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
  }, 'GETTING_ESIGN_DOCS', {
    fallbackAction: logout,
    errorCodes: [401],
    requestId,
  });
}

export function getOnboardingDocList(next, search = '') {
  const searchUrlParam = search ? `&search=${encodeURIComponent(search)}` : '';
  const token = getToken();
  const companyId = get(window, 'asure.appSettings.selectedCompany.companyId');
  const url = next || `${buildApiUrl(token, companyId)}/templates?consolidated=true&encodeIds=false${searchUrlParam}`;
  return createFetchAction(GET_ONBOARDING_DOC_LIST, {
    url,
    method: 'GET',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
  }, 'GETTING_ESIGN_DOCS', {
    fallbackAction: logout,
    errorCodes: [401],
    search,
  });
}

export function getTaskListDocs() {
  const token = getToken();
  const companyId = get(window, 'asure.appSettings.selectedCompany.companyId');
  const taskListId = get(window, 'asure.appSettings.esignature.taskList.id');
  return createFetchAction(GET_TASKLIST_DOCS, {
    url: `${buildApiUrl(token, companyId)}/documents?docType=all&categoryId=${taskListId}`,
    method: 'GET',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
  }, 'GETTING_TASKLIST_DOCS', {
    fallbackAction: logout,
    errorCodes: [401],
  });
}

export function getCompanyDocCategories(next) {
  const token = getToken();
  const companyId = get(window, 'asure.appSettings.selectedCompany.companyId');
  const url = next || `${buildApiUrl(token, companyId)}/categories`;
  return createFetchAction(GET_COMPANY_DOCUMENT_CATEGORIES, {
    url,
    method: 'GET',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
  }, 'GETTING_COMPANY_DOCUMENT_CATEGORIES', {
    fallbackAction: logout,
    errorCodes: [401],
  });
}

export function getEmployeeDocCategories(
  next,
  companyId = get(window, 'asure.appSettings.selectedCompany.companyId'),
  employeeId = get(window, 'asure.appSettings.selectedEmployee.employeeId'),
) {
  const token = getToken();
  let url = next;
  if (companyId && employeeId) {
    url = `${buildTenantApiUri(token)}/companies/${companyId}/employees/${employeeId}/documents/categories`;
  } else if (companyId) {
    url = `${buildTenantApiUri(token)}/companies/${companyId}/documents/categories`;
  }
  return createFetchAction(GET_EMPLOYEE_DOCUMENT_CATEGORIES, {
    url,
    method: 'GET',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
  }, 'GETTING_EMPLOYEE_DOCUMENT_CATEGORIES', {
    fallbackAction: logout,
    errorCodes: [401],
    companyId,
  });
}

export function clearCategories() {
  return {
    type: CLEAR_CATEGORIES,
  };
}

export function pushRecentDocCategory(category) {
  return { type: PUSH_RECENT_DOC_CATEGORY, category };
}

export function saveTemplateMetadata(postBody) {
  const {
    fileName, title, category, id, isOnboardingDocument,
  } = postBody;
  const token = getToken();
  const companyId = get(window, 'asure.appSettings.selectedCompany.companyId');
  return createFetchAction(SAVE_TEMPLATE_METADATA, {
    url: `${buildApiUrl(token, companyId)}/templates/${id}/save-metadata`,
    method: 'POST',
    body: JSON.stringify({
      fileName,
      title,
      category,
      isOnboardingDocument,
    }),
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
  }, 'SAVING_TEMPLATE_METADATA', {
    fallbackAction: logout,
    errorCodes: [401],
  });
}

function buildDocsUrl(
  type, token,
  employeeId = get(window, 'asure.appSettings.selectedEmployee.employeeId'),
  companyId = get(window, 'asure.appSettings.selectedCompany.companyId'),
) {
  let url = `${buildTenantApiUri(token)}/documents`;
  switch (type) {
    case 'company': {
      url = `${buildTenantApiUri(token)}/companies/${companyId}/documents`;
      break;
    }
    case 'employee': {
      url = `${buildTenantApiUri(token)}/companies/${companyId}/employees/${employeeId}/documents`;
      break;
    }
    default:
      break;
  }
  return url;
}

function determineRoleQueryParam(role) {
  if (role && role === 'Manager') {
    return 'role=manager';
  }
  return '';
}

export function getEmployeeDocuments(type, next, query = '', requestId = '') {
  const token = getToken();
  let queryParam = query ? `?search=${query}` : '';
  if (type === 'company') {
    const { menu: role } = queryString.parse(window.location.search);
    if (queryParam) {
      queryParam = role ? `${queryParam}&${determineRoleQueryParam(role)}` : queryParam;
    } else {
      queryParam = role ? `?${determineRoleQueryParam(role)}` : '';
    }
  }
  const url = next || buildDocsUrl(type, token) + queryParam;
  return createFetchAction(GET_EMPLOYEE_DOCUMENTS, {
    url,
    method: 'GET',
    headers: {
      Authorization: `Bearer ${token}`,
    },
  }, 'GETTING_EMPLOYEE_DOCUMENTS', {
    fallbackAction: logout,
    errorCodes: [401],
    requestId,
  });
}

export function refreshEmployeeDocuments(type, next, query = '', requestId = '') {
  const token = getToken();
  let queryParam = query ? `?search=${query}` : '';
  if (type === 'company') {
    const { menu: role } = queryString.parse(window.location.search);
    if (queryParam) {
      queryParam = role ? `${queryParam}&${determineRoleQueryParam(role)}` : queryParam;
    } else {
      queryParam = role ? `?${determineRoleQueryParam(role)}` : '';
    }
  }
  const url = next || buildDocsUrl(type, token) + queryParam;
  return createFetchAction(REFRESH_EMPLOYEE_DOCUMENTS, {
    url,
    method: 'GET',
    headers: {
      Authorization: `Bearer ${token}`,
    },
  }, 'REFRESHING_EMPLOYEE_DOCUMENTS', {
    fallbackAction: logout,
    errorCodes: [401],
    requestId,
  });
}

export function getDocumentPreviewUrl(type, requestId) {
  const token = getToken();
  return createFetchAction(GET_DOCUMENT_PREVIEW_URL, {
    url: `${buildDocsUrl(type, token)}/${requestId}/preview`,
    method: 'GET',
    headers: {
      Authorization: `Bearer ${token}`,
    },
  }, 'GETTING_DOCUMENT_PREVIEW_URL', {
    fallbackAction: logout,
    errorCodes: [401],
  });
}

export function getOnboardingDocumentPreviewUrl(requestId) {
  const token = getToken();
  const onboardingKey = get(window, 'asure.appSettings.esignature.onboarding.key') || queryString.parse(window.location.search).onboardingkey;
  return createFetchAction(GET_DOCUMENT_PREVIEW_URL, {
    url: `${buildDocsUrl('', token)}/${requestId}/onboarding-preview`,
    method: 'POST',
    body: JSON.stringify({
      onboardingKey,
    }),
  }, 'GETTING_ONBOARDING_DOCUMENT_PREVIEW_URL', {
    fallbackAction: logout,
    errorCodes: [401],
  });
}

export function getDocumentDownloadUrl(type, requestId, fileName) {
  const token = getToken();
  return createFetchAction(GET_DOCUMENT_DOWNLOAD_URL, {
    url: `${buildDocsUrl(type, token)}/${requestId}/preview`,
    method: 'GET',
    headers: {
      Authorization: `Bearer ${token}`,
    },
  }, 'GETTING_DOCUMENT_DOWNLOAD_URL', {
    fallbackAction: logout,
    errorCodes: [401],
    fileName,
  });
}

export function getOnboardingDocumentDownloadUrl(requestId, fileName) {
  const token = getToken();
  const onboardingKey = get(window, 'asure.appSettings.esignature.onboarding.key') || queryString.parse(window.location.search).onboardingkey;
  return createFetchAction(GET_DOCUMENT_DOWNLOAD_URL, {
    url: `${buildDocsUrl('', token)}/${requestId}/onboarding-preview`,
    method: 'POST',
    body: JSON.stringify({
      onboardingKey,
    }),
  }, 'GETTING_ONBOARDING_DOCUMENT_DOWNLOAD_URL', {
    fallbackAction: logout,
    errorCodes: [401],
    fileName,
  });
}

export function createSimpleSignDocument(documentId) {
  const token = getToken();
  return createFetchAction(CREATE_SIMPLE_SIGN_DOCUMENT, {
    url: buildDocsUrl('employee', token),
    method: 'POST',
    headers: { Authorization: `Bearer ${token}` },
    body: JSON.stringify({
      signatureRequestId: documentId,
      timeZone: moment.tz.guess(),
    }),
  }, 'CREATING_SIMPLE_SIGN_DOCUMENT', {
    fallbackAction: logout,
    errorCodes: [401],
    failureMessage: simpleSignError,
    documentId,
  });
}

function createUploadUrlAction({
  url,
  body,
  fileName,
  file,
}) {
  const token = getToken();
  const action = createFetchAction(GET_UPLOAD_URL, {
    url,
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(body),
  }, 'GETTING_UPLOAD_URL', {
    fallbackAction: logout,
    errorCodes: [401],
    fileName,
  });

  const mimeType = mime.contentType(fileName);
  const dataPrefixRegex = new RegExp(`^data:(${mimeType});base64,`);
  const data = Buffer.from(file.replace(dataPrefixRegex, ''), 'base64');

  return {
    action,
    data,
    mimeType,
  };
}

export function sendReminderEmail(
  companyId,
  employeeId,
  documentId,
) {
  const token = getToken();
  const url = `${buildDocsUrl('employee', token, employeeId, companyId)}/${documentId}/remind`;
  return createFetchAction(SEND_REMINDER_EMAIL, {
    url,
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
    },
  }, 'SENDING_REMINDER_EMAIL', {
    fallbackAction: logout,
    errorCodes: [401],
  });
}

function reloadUpdatedEmployeeDocuments() {
  return ({ dispatch }) => {
    const employeeId = get(window, 'asure.appSettings.selectedEmployee.employeeId');
    const companyId = get(window, 'asure.appSettings.selectedCompany.companyId');
    let option = 'tenant';

    if (companyId) {
      option = 'company';
    }

    if (employeeId) {
      option = 'employee';
    }

    setTimeout(() => {
      dispatch(refreshEmployeeDocuments(option));
    }, 3000);
  };
}

function reloadUpdatedCompanyDocuments() {
  return ({ dispatch }) => {
    setTimeout(() => {
      dispatch(getEsignDocs());
    }, 3000);
  };
}

export function uploadEmployeeDocument(payload, employeeId, companyId, category, isPrivate = false) {
  const token = getToken();
  const { encodedFile: file, file: { name: fileName }, title } = payload;
  const url = `${buildDocsUrl('company', token, employeeId, companyId)}/upload-url`;

  const { action: getUploadUrl, data, mimeType } = createUploadUrlAction({
    url,
    body: {
      fileName,
      title,
      employeeId: Number(employeeId),
      isPrivate,
      category,
    },
    fileName,
    file,
  });

  return async (dispatch) => {
    const uploadDocument = ({ result }) => {
      const { url: presignedUrl } = result;
      const action = createFetchAction(UPLOAD_EMPLOYEE_DOCUMENT, {
        url: presignedUrl,
        method: 'PUT',
        headers: {
          'Content-Type': mimeType,
        },
        body: data,
      }, 'UPLOADING_EMPLOYEE_DOCUMENT', {
        fallbackAction: logout,
        errorCodes: [401],
        fileName,
      });

      const followUpAction = reloadUpdatedEmployeeDocuments();
      action(dispatch, followUpAction);
    };
    await getUploadUrl(dispatch, uploadDocument);
  };
}

export function uploadSimpleSignatureRequest(payload, companyId, category, isPrivate = true) {
  const token = getToken();
  const { encodedFile: file, file: { name: fileName }, title } = payload;
  const url = `${buildDocsUrl('company', token, 0, companyId)}/upload-url`;

  const { action: getUploadUrl, data, mimeType } = createUploadUrlAction({
    url,
    body: {
      fileName,
      title,
      category,
      isPrivate,
    },
    fileName,
    file,
  });

  return async (dispatch) => {
    const uploadDocument = ({ result }) => {
      const { url: presignedUrl } = result;
      const action = createFetchAction(UPLOAD_SIMPLE_SIGNATURE_REQUEST, {
        url: presignedUrl,
        method: 'PUT',
        headers: {
          'Content-Type': mimeType,
        },
        body: data,
      }, 'UPLOADING_SIMPLE_SIGNATURE_REQUEST', {
        fallbackAction: logout,
        errorCodes: [401],
        fileName,
      });

      const followUpAction = reloadUpdatedCompanyDocuments();
      action(dispatch, followUpAction);
    };
    await getUploadUrl(dispatch, uploadDocument);
  };
}

export function uploadCompanyDocument(payload, companyId, category, isPublishedToEmployee = false) {
  const token = getToken();
  const { encodedFile: file, file: { name: fileName }, title } = payload;
  return createFetchAction(UPLOAD_COMPANY_DOCUMENT, {
    url: `${buildDocsUrl('company', token, 0, companyId)}`,
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      file,
      fileName,
      title,
      category,
      isPublishedToEmployee,
    }),
  }, 'UPLOADING_COMPANY_DOCUMENT', {
    fallbackAction: logout,
    errorCodes: [401],
    fileName,
  });
}

export function updateCompanyDocument(payload = {}, companyId, title, category, isPublishedToEmployee, documentId, isOnboardingDocument) {
  const token = getToken();
  const { encodedFile: file, file: { name: fileName } = {} } = payload;
  return createFetchAction(UPDATE_COMPANY_DOCUMENT, {
    url: `${buildDocsUrl('company', token, 0, companyId)}/${documentId}`,
    method: 'PATCH',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      fileObject: file ? {
        file,
        fileName,
      } : undefined,
      title,
      category,
      isPublishedToEmployee,
      isOnboardingDocument,
    }),
  }, 'UPDATING_COMPANY_DOCUMENT', {
    fallbackAction: logout,
    errorCodes: [401],
  });
}

export function updateEmployeeDocument(payload = {}, companyId, employeeId, title, isPrivate, documentId, category) {
  const token = getToken();
  const { encodedFile: file, file: { name: fileName } = {} } = payload;

  /**
   * if file object:
   * get presigned url with document id as metadata
   * upload with presigned url
   *
   * if no file object:
   * call endpoint with title & isPrivate flag
   */

  if (file) {
    const url = `${buildDocsUrl('company', token, employeeId, companyId)}/upload-url`;

    const { action: getUploadUrl, data, mimeType } = createUploadUrlAction({
      url,
      body: {
        fileName,
        title,
        employeeId: Number(employeeId),
        isPrivate,
        documentId,
        category,
      },
      fileName,
      file,
    });

    return async (dispatch) => {
      const updateDocument = ({ result }) => {
        const { url: presignedUrl } = result;
        const action = createFetchAction(UPDATE_EMPLOYEE_DOCUMENT, {
          url: presignedUrl,
          method: 'PUT',
          headers: {
            'Content-Type': mimeType,
          },
          body: data,
        }, 'UPDATING_EMPLOYEE_DOCUMENT', {
          fallbackAction: logout,
          errorCodes: [401],
          fileName,
        });

        const followUpAction = reloadUpdatedEmployeeDocuments();
        action(dispatch, followUpAction);
      };
      await getUploadUrl(dispatch, updateDocument);
    };
  } else {
    return createFetchAction(UPDATE_EMPLOYEE_DOCUMENT, {
      url: `${buildDocsUrl('employee', token, employeeId, companyId)}/${documentId}`,
      method: 'PATCH',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        title,
        isPrivate,
        category,
      }),
    }, 'UPDATING_EMPLOYEE_DOCUMENT', {
      fallbackAction: logout,
      errorCodes: [401],
    });
  }
}

export function deleteCompanyDocument(companyId, documentId) {
  const token = getToken();
  return createFetchAction(DELETE_COMPANY_DOCUMENT, {
    url: `${buildDocsUrl('company', token, undefined, companyId)}/${documentId}`,
    method: 'DELETE',
    headers: {
      Authorization: `Bearer ${token}`,
    },
  }, 'DELETING_COMPANY_DOCUMENT', {
    fallbackAction: logout,
    errorCodes: [401],
    documentId,
  });
}

export function deleteEmployeeDocument(companyId, employeeId, documentId) {
  const token = getToken();
  return createFetchAction(DELETE_EMPLOYEE_DOCUMENT, {
    url: `${buildDocsUrl('employee', token, employeeId, companyId)}/${documentId}`,
    method: 'DELETE',
    headers: {
      Authorization: `Bearer ${token}`,
    },
  }, 'DELETING_EMPLOYEE_DOCUMENT', {
    fallbackAction: logout,
    errorCodes: [401],
    documentId,
  });
}

export function updateSignatureRequestStatus(requestId, stepNumber) {
  const token = getToken();
  const companyId = get(window, 'asure.appSettings.selectedCompany.companyId');
  const employeeId = get(window, 'asure.appSettings.selectedEmployee.employeeId');
  return createFetchAction(UPDATE_SIGNATURE_REQUEST_STATUS, {
    url: `${buildApiUrl(token, companyId, employeeId)}/requests/${requestId}/status`,
    method: 'PATCH',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      stepNumber,
    }),
  }, 'UPDATING_SIGNATURE_REQUEST_STATUS', {
    fallbackAction: logout,
    errorCodes: [401],
  });
}

export function setSelectedDocument(document) {
  return {
    type: SET_SELECTED_DOCUMENT,
    payload: document,
  };
}

const INITIAL_STATE = {
  docCategories: {
    recent: [],
    categories: [],
    next: '',
    currentCompany: '',
  },
  taskListDocs: [],
  esignDocs: {
    results: [],
    next: '',
  },
  onboardingDocs: {
    results: [],
    next: '',
    queryNext: {},
  },
  employeeDocs: {
    results: [],
    next: '',
  },
  filePreview: {},
  fileDownload: {},
  selectedDocument: {},
};

export function docs(state = INITIAL_STATE, action) {
  switch (action.type) {
    case GET_ESIGN_DOCS: {
      const { payload, payload: { next } = {} } = action;
      const { esignDocs: { results } } = state;
      const payloadResults = payload && payload.results ? payload.results : [];
      return {
        ...state,
        esignDocs: {
          results: uniqBy([
            ...payloadResults,
            ...results,
          ], 'id'),
          next,
        },
      };
    }
    case GET_ONBOARDING_DOC_LIST: {
      const { payload, payload: { next } = {}, meta: { search } } = action;
      const { onboardingDocs: { results, queryNext } } = state;
      const payloadResults = payload && payload.results ? payload.results : [];
      return {
        ...state,
        onboardingDocs: {
          results: uniqBy([
            ...results,
            ...payloadResults,
          ], 'id'),
          next,
          queryNext: {
            ...queryNext,
            [search]: next,
          },
        },
      };
    }
    case CREATE_SIMPLE_SIGN_DOCUMENT: {
      const { payload } = action;
      if (!payload) {
        return state;
      }
      const { employeeDocs: { results, next } } = state;
      const documents = results.map(document => (document.id === payload.id ? { ...document, ...payload } : document));
      return {
        ...state,
        employeeDocs: {
          results: documents,
          next,
        },
      };
    }
    case SAVE_TEMPLATE_METADATA: {
      const { payload } = action;
      const { esignDocs: { results } } = state;
      return {
        ...state,
        esignDocs: {
          results: uniqBy([
            ...[payload],
            ...results,
          ], 'id'),
        },
      };
    }
    case GET_TASKLIST_DOCS: {
      const { payload } = action;
      return {
        ...state,
        taskListDocs: payload && payload.results ? payload.results : [],
      };
    }
    case GET_EMPLOYEE_DOCUMENTS: {
      const { payload, payload: { next } = {} } = action;
      const { employeeDocs: { results } } = state;
      const payloadResults = payload && payload.results ? payload.results : [];
      return {
        ...state,
        employeeDocs: {
          results: [
            ...payloadResults,
            ...results,
          ],
          next,
        },
      };
    }
    case REFRESH_EMPLOYEE_DOCUMENTS: {
      const { payload, payload: { next } = {} } = action;
      const { employeeDocs: { results } } = state;
      const payloadResults = payload && payload.results ? payload.results : [];
      return {
        ...state,
        employeeDocs: {
          results: uniqBy([
            ...payloadResults,
            ...results,
          ], 'id'),
          next,
        },
      };
    }
    case GET_DOCUMENT_PREVIEW_URL: {
      const { payload: { data: previewUrl, mimeType } } = action;
      return {
        ...state,
        filePreview: {
          url: previewUrl,
          mimeType,
        },
      };
    }
    case GET_DOCUMENT_DOWNLOAD_URL: {
      const { payload: { data: downloadUrl }, meta: { fileName } } = action;
      return {
        ...state,
        fileDownload: {
          url: downloadUrl,
          fileName,
        },
      };
    }

    case UPLOAD_EMPLOYEE_DOCUMENT: {
      const { payload } = action;
      const { employeeDocs: { results, next } } = state;
      const payloadResults = payload && payload.results ? payload.results : [];
      return {
        ...state,
        employeeDocs: {
          results: [
            ...payloadResults,
            ...results,
          ],
          next,
        },
      };
    }

    case UPLOAD_SIMPLE_SIGNATURE_REQUEST: {
      const { payload } = action;
      const { esignDocs: { results, next } } = state;
      const payloadResults = payload && payload.results ? payload.results : [];
      return {
        ...state,
        esignDocs: {
          results: [
            ...payloadResults,
            ...results,
          ],
          next,
        },
      };
    }

    case GET_COMPANY_DOCUMENT_CATEGORIES: {
      const { payload, payload: { next } = {} } = action;
      const { docCategories: { categories, recent } } = state;
      const payloadResults = payload && payload.results ? payload.results : [];
      return {
        ...state,
        docCategories: {
          categories: uniqBy([
            ...categories,
            ...recent,
            ...payloadResults,
          ], 'value'),
          recent,
          next,
        },
      };
    }
    case GET_EMPLOYEE_DOCUMENT_CATEGORIES: {
      const { payload, payload: { next } = {}, meta: { companyId } } = action;
      const { docCategories: { currentCompany, categories, recent } } = state;
      const payloadResults = payload && payload.results ? payload.results : [];
      if (companyId !== currentCompany) {
        return {
          ...state,
          docCategories: {
            categories: payloadResults,
            recent,
            next,
            currentCompany: companyId,
          },
        };
      }
      return {
        ...state,
        docCategories: {
          categories: uniqBy([
            ...categories,
            ...recent,
            ...payloadResults,
          ], 'value'),
          recent,
          next,
        },
      };
    }
    case CLEAR_CATEGORIES: {
      return {
        ...state,
        docCategories: {
          categories: [],
          recent: [],
          next: '',
          currentCompany: '',
        },
      };
    }
    case PUSH_RECENT_DOC_CATEGORY: {
      const { category } = action;
      const { docCategories: { recent, categories, next } } = state;
      return {
        ...state,
        docCategories: {
          categories: categories.filter(e => (recent.indexOf(e) < 0 && e !== category)),
          recent: uniqBy([...recent, category], 'value'),
          next,
        },
      };
    }
    case UPLOAD_COMPANY_DOCUMENT: {
      const { payload } = action;
      const { esignDocs: { results, next } } = state;
      return {
        ...state,
        esignDocs: {
          results: [
            payload,
            ...results,
          ],
          next,
        },
      };
    }
    case UPDATE_COMPANY_DOCUMENT: {
      const { payload } = action;
      const { esignDocs: { results, next } } = state;
      const documents = results.map(document => (document.id === payload.id ? { ...document, ...payload } : document));
      return {
        ...state,
        esignDocs: {
          results: documents,
          next,
        },
      };
    }
    case UPDATE_EMPLOYEE_DOCUMENT: {
      const { payload } = action;
      if (!payload) {
        return state;
      }
      const { employeeDocs: { results, next } } = state;
      const documents = results.map(document => (document.id === payload.id ? { ...document, ...payload } : document));
      return {
        ...state,
        employeeDocs: {
          results: documents,
          next,
        },
      };
    }
    case DELETE_COMPANY_DOCUMENT: {
      const { meta: { documentId } } = action;
      const { esignDocs: { results, next } } = state;
      const documents = results.filter(document => document.id !== documentId);
      return {
        ...state,
        esignDocs: {
          results: documents,
          next,
        },
      };
    }
    case DELETE_EMPLOYEE_DOCUMENT: {
      const { meta: { documentId } } = action;
      const { employeeDocs: { results, next } } = state;
      const documents = results.filter(document => document.id !== documentId);
      return {
        ...state,
        employeeDocs: {
          results: documents,
          next,
        },
      };
    }
    case UPDATE_SIGNATURE_REQUEST_STATUS: {
      const { payload: { id, status } } = action;
      const { employeeDocs: { results, next } } = state;
      const updatedDocs = results.map((doc) => {
        const updatedDoc = doc;
        if (doc.id === id) {
          updatedDoc.status = status;
        }
        return updatedDoc;
      });
      return {
        ...state,
        employeeDocs: {
          results: [...updatedDocs],
          next,
        },
      };
    }
    case SET_SELECTED_DOCUMENT: {
      const { payload } = action;
      return {
        ...state,
        selectedDocument: payload,
      };
    }
    default:
      break;
  }
  return state;
}




// WEBPACK FOOTER //
// ./src/modules/docs/docs.js